import type {
  FormDescriptor,
  FormErrorDescriptor,
  FormFieldDescriptor,
  FormSubmissionResult,
} from '@finance/contracts';
import { Decimal } from '@finance/domain-core';

const NON_NEGATIVE_DECIMAL = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

export interface FinancialCollectionError {
  readonly fieldName: string;
  readonly error: FormErrorDescriptor;
}

function journalAmount(value: unknown): Decimal | null {
  if (value === undefined || value === null || value === '') return Decimal.zero();
  const normalized = String(value).trim();
  if (!NON_NEGATIVE_DECIMAL.test(normalized)) return null;
  try {
    return Decimal.fromString(normalized);
  } catch {
    return null;
  }
}

function validateJournalBalanceField(
  field: FormFieldDescriptor,
  value: unknown,
): FinancialCollectionError | null {
  if (field.collection?.summaryMode !== 'journalBalance') return null;
  if (!Array.isArray(value)) {
    return {
      fieldName: field.name,
      error: { code: 'form.collection.invalid' as never },
    };
  }

  let totalDebit = Decimal.zero();
  let totalCredit = Decimal.zero();

  for (let index = 0; index < value.length; index += 1) {
    const row = value[index];
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return {
        fieldName: field.name,
        error: {
          code: 'form.collection.invalidRow' as never,
          params: { index: index + 1 },
        },
      };
    }

    const record = row as Record<string, unknown>;
    const debit = journalAmount(record.debit);
    const credit = journalAmount(record.credit);
    if (!debit || !credit) {
      return {
        fieldName: field.name,
        error: {
          code: 'form.collection.journalAmountInvalid' as never,
          params: { index: index + 1 },
        },
      };
    }
    if (debit.isZero() === credit.isZero()) {
      return {
        fieldName: field.name,
        error: {
          code: 'form.collection.journalLineInvalid' as never,
          params: { index: index + 1 },
        },
      };
    }
    totalDebit = totalDebit.plus(debit);
    totalCredit = totalCredit.plus(credit);
  }

  if (totalDebit.isZero() || totalCredit.isZero()) {
    return {
      fieldName: field.name,
      error: { code: 'form.collection.journalTotalRequired' as never },
    };
  }
  if (!totalDebit.eq(totalCredit)) {
    return {
      fieldName: field.name,
      error: { code: 'form.collection.journalUnbalanced' as never },
    };
  }
  return null;
}

/**
 * Fail-closed financial collection validation applied before any descriptor
 * form command dispatch. Field adapters still provide immediate cell/editor
 * feedback; this boundary guarantees a renderer cannot bypass accounting
 * invariants merely by using a different presentation (for example a stepped
 * workflow).
 */
export function validateFinancialCollections(
  contract: FormDescriptor,
  values: Record<string, unknown>,
  fieldNames?: ReadonlySet<string>,
): FinancialCollectionError | null {
  for (const field of contract.fields) {
    if (fieldNames && !fieldNames.has(field.name)) continue;
    if (!(field.name in values)) continue;
    const error = validateJournalBalanceField(field, values[field.name]);
    if (error) return error;
  }
  return null;
}

export function financialCollectionFailure(
  error: FinancialCollectionError,
): FormSubmissionResult {
  return {
    status: 'failed',
    fieldErrors: { [error.fieldName]: error.error },
  };
}
